import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { SandboxedView } from "./SandboxedView";
import { CustomViewCreatorDialog } from "./CustomViewCreatorDialog";
import {
  useCustomViews,
  useDeleteCustomView,
  useReorderCustomViews,
  type CustomView,
} from "@/hooks/useCustomViews";
import { toast } from "sonner";

interface CustomViewsGridProps {
  userId: string;
  userCredits?: number;
  isPremium?: boolean;
}

export const CustomViewsGrid = ({
  userId,
  userCredits,
  isPremium,
}: CustomViewsGridProps) => {
  const { t } = useTranslation();
  const { data: views = [] } = useCustomViews(userId);
  const deleteView = useDeleteCustomView(userId);

  // Refine dialog state
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineView, setRefineView] = useState<CustomView | null>(null);

  const reorder = useReorderCustomViews(userId);

  const handleMove = async (viewIndex: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? viewIndex - 1 : viewIndex + 1;
    if (targetIndex < 0 || targetIndex >= views.length) return;

    const view1 = views[viewIndex];
    const view2 = views[targetIndex];

    try {
      await reorder.mutateAsync({
        viewId1: view1.id,
        order1: view1.display_order,
        viewId2: view2.id,
        order2: view2.display_order,
      });
    } catch {
      toast.error(t('custom_views.failed_to_reorder', 'Failed to reorder widget'));
    }
  };

  if (views.length === 0) return null;

  const handleDelete = async (viewId: string) => {
    try {
      await deleteView.mutateAsync(viewId);
    } catch {
      // silent
    }
  };

  const handleEdit = (view: CustomView) => {
    setRefineView(view);
    setRefineOpen(true);
  };

  return (
    <>
      <div className="bg-card border-x border-border px-3 py-3">
        <div className="grid grid-cols-2 gap-3">
          <AnimatePresence mode="popLayout">
            {views.map((view, index) => {
              const isHalf = view.size === 'half';
              const canMoveUp = index > 0;
              const canMoveDown = index < views.length - 1;

              return (
                <motion.div
                  key={view.id}
                  layout
                  className={isHalf ? '' : 'col-span-2'}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <SandboxedView
                    htmlContent={view.html_content}
                    maxHeight={260}
                    interactive
                    isHalf={isHalf}
                    explanation={view.explanation}
                    onDelete={() => handleDelete(view.id)}
                    onEdit={() => handleEdit(view)}
                    onMoveUp={canMoveUp ? () => handleMove(index, 'up') : undefined}
                    onMoveDown={canMoveDown ? () => handleMove(index, 'down') : undefined}
                    onMoveLeft={isHalf && canMoveUp ? () => handleMove(index, 'up') : undefined}
                    onMoveRight={isHalf && canMoveDown ? () => handleMove(index, 'down') : undefined}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Refine dialog */}
      {refineView && (
        <CustomViewCreatorDialog
          open={refineOpen}
          onOpenChange={(open) => {
            setRefineOpen(open);
            if (!open) setRefineView(null);
          }}
          userId={userId}
          userCredits={userCredits}
          isPremium={isPremium}
          existingViewId={refineView.id}
          existingViewHtml={refineView.html_content}
          existingViewTitle={refineView.title}
          existingViewSize={refineView.size}
          existingViewExplanation={refineView.explanation}
          initialPrompt=""
        />
      )}
    </>
  );
};

